import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createCharacter, getCharacter, updateCharacter, getCampaign } from '../lib/api';
import CharacterForm from '../components/CharacterForm';
import { CharacterWithSkills, SkillProficiencies, ClassName, SkillName } from '@shared/types';

export default function CharacterEditPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [character, setCharacter] = useState<CharacterWithSkills | null>(null);
  const [campaignLevel, setCampaignLevel] = useState<number>(1);
  const [formVersion, setFormVersion] = useState(0);

  useEffect(() => {
    if (!slug) return;

    // WHY: Fetch campaign level alongside character so saving throw previews use correct proficiency bonus
    getCampaign(slug)
      .then((data) => {
        setCampaignLevel(data.campaign.level);
        if (!isNew && id) {
          return getCharacter(id);
        }
        return null;
      })
      .then((char) => {
        if (char) {
          setCharacter(char);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [id, isNew, slug]);

  const handleSubmit = async (data: {
    name: string;
    class: ClassName;
    abilityScores: Record<string, number>;
    skills: SkillProficiencies;
    savingThrowOverrides: Record<string, number | null>;
    skillBonusOverrides: Record<SkillName, number | null>;
    languages: string[];
    toolProficiencies: string[];
  }) => {
    if (!slug) return;
    setSaving(true);
    setError(null);

    try {
      if (isNew) {
        const created = await createCharacter(slug, {
          name: data.name,
          class: data.class,
          strength: data.abilityScores.strength,
          dexterity: data.abilityScores.dexterity,
          constitution: data.abilityScores.constitution,
          intelligence: data.abilityScores.intelligence,
          wisdom: data.abilityScores.wisdom,
          charisma: data.abilityScores.charisma,
          skills: data.skills,
          languages: data.languages,
          toolProficiencies: data.toolProficiencies,
        });
        navigate(`/${slug}/character/${created.id}`);
      } else if (id) {
        await updateCharacter(id, {
          name: data.name,
          class: data.class,
          strength: data.abilityScores.strength,
          dexterity: data.abilityScores.dexterity,
          constitution: data.abilityScores.constitution,
          intelligence: data.abilityScores.intelligence,
          wisdom: data.abilityScores.wisdom,
          charisma: data.abilityScores.charisma,
          saving_throw_override_str: data.savingThrowOverrides.strength,
          saving_throw_override_dex: data.savingThrowOverrides.dexterity,
          saving_throw_override_con: data.savingThrowOverrides.constitution,
          saving_throw_override_int: data.savingThrowOverrides.intelligence,
          saving_throw_override_wis: data.savingThrowOverrides.wisdom,
          saving_throw_override_cha: data.savingThrowOverrides.charisma,
          skills: data.skills,
          skillBonusOverrides: data.skillBonusOverrides,
          languages: data.languages,
          toolProficiencies: data.toolProficiencies,
        });
        setCharacter((prev) =>
          prev
            ? {
                ...prev,
                saving_throw_override_str: data.savingThrowOverrides.strength,
                saving_throw_override_dex: data.savingThrowOverrides.dexterity,
                saving_throw_override_con: data.savingThrowOverrides.constitution,
                saving_throw_override_int: data.savingThrowOverrides.intelligence,
                saving_throw_override_wis: data.savingThrowOverrides.wisdom,
                saving_throw_override_cha: data.savingThrowOverrides.charisma,
                ...data,
              }
            : prev
        );
        setFormVersion((v) => v + 1);
      }
    } catch (err: any) {
      setError(err.message);
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="heading text-2xl font-bold">
          {isNew ? 'Create Character' : 'Edit Character'}
        </h2>
        {slug && (
          <Link to={`/${slug}`} className="btn btn-secondary">
            Back to Campaign
          </Link>
        )}
      </div>

      {error && (
        <div className="bg-dnd-danger-bg border border-dnd-danger-border text-dnd-danger-text px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <CharacterForm
        key={character ? `${character.id}-${formVersion}` : `new-${formVersion}`}
        initialData={character}
        onSubmit={handleSubmit}
        saving={saving}
        campaignLevel={campaignLevel}
      />
    </div>
  );
}
